/**
 * Configuración de Keycloak para el SSO institucional.
 *
 * Login exclusivamente por la **interfaz nativa de Keycloak** (redirect completo
 * a la pantalla hosteada por Keycloak, flujo Authorization Code + PKCE S256).
 * No hay formulario propio ni `grant_type=password`.
 *
 * Estándar del workspace:
 *   - AutenticacionSSO_Backend/KEYCLOAK_SSO_SETUP.md §2 (integración frontend)
 *   - AutenticacionSSO_Backend/KEYCLOAK_LOGIN_STANDARD.md §3 (Camino 2 / Opción A)
 *
 * Camino 2 / Opción A: el access token de Keycloak es la única credencial. Se
 * manda tal cual al backend de la Biblioteca PQR, que verifica su firma contra
 * el JWKS del realm en cada request y exige el rol de cliente `pqr.ingresar`.
 *
 * ── Por qué la configuración es de runtime y no de build ─────────────────────
 * Esta app no usa Vite: el bundle lo arma Bun y se sirve estático desde nginx,
 * así que no hay `import.meta.env`. Las variables llegan en `/config.json`
 * (`window.__env__`), que `src/frontend.tsx` resuelve antes de arrancar; por eso
 * la instancia de Keycloak se construye dentro de {@link initKeycloak} y no en
 * el tope del módulo, donde `window.__env__` todavía no existiría.
 */

import Keycloak from "keycloak-js";

type EnvRuntime = {
  API_BASE_URL?: string;
  KEYCLOAK_URL?: string;
  KEYCLOAK_REALM?: string;
  KEYCLOAK_CLIENT_ID?: string;
  SSO_APPS_URL?: string;
  REDIRECT_TO_APPS?: string;
};

function env(): EnvRuntime {
  return (window as any).__env__ ?? {};
}

/**
 * Identifica a esta app dentro del realm compartido.
 *
 * Se expone porque los roles de autorización viven en
 * `resource_access[clientId].roles`: sin este valor no se puede distinguir un
 * rol propio de uno homónimo de otra app del realm.
 */
export function getClientId(): string {
  return env().KEYCLOAK_CLIENT_ID ?? "";
}

/**
 * Portal que lista todas las apps del workspace. Destino del ítem "Todas las
 * Apps" del menú de usuario, del logout explícito, del arranque sin sesión
 * previa (`REDIRECT_TO_APPS`) y de cualquier pérdida de sesión detectada en
 * caliente.
 */
export function getSsoAppsUrl(): string {
  return env().SSO_APPS_URL ?? "";
}

/** Si es `"true"`, sin sesión activa se redirige al portal de apps en vez de al login nativo de Keycloak. */
function redirigirAlPortal(): boolean {
  return env().REDIRECT_TO_APPS?.toLowerCase() === "true";
}

/**
 * Segundos de validez restante por debajo de los cuales se renueva el token.
 * Una consulta al chatbot puede tardar decenas de segundos: sin este margen el
 * token alcanzaría a vencer entre que se arma el request y el backend lo lee.
 */
const MIN_VALIDITY_SEGUNDOS = 70;

/** Cada cuánto se revisa si hace falta renovar. */
const INTERVALO_RENOVACION_MS = 30_000;

export type ErrorSso = {
  titulo: string;
  detalle: string;
};

/** Falta configuración: se detecta antes de intentar hablar con Keycloak. */
export function getConfigFaltante(): string[] {
  const { KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID, SSO_APPS_URL } = env();

  return [
    !KEYCLOAK_URL && "KEYCLOAK_URL",
    !KEYCLOAK_REALM && "KEYCLOAK_REALM",
    !KEYCLOAK_CLIENT_ID && "KEYCLOAK_CLIENT_ID",
    !SSO_APPS_URL && "SSO_APPS_URL",
  ].filter((v): v is string => Boolean(v));
}

let instancia: Keycloak | null = null;

/**
 * Instancia viva de Keycloak.
 *
 * Lanza si se la pide antes de {@link initKeycloak}, que es siempre un error de
 * programación: toda la app se monta después de que la sesión está resuelta.
 */
export function getKeycloak(): Keycloak {
  if (!instancia) {
    throw new Error("Keycloak no está inicializado: initKeycloak() debe correr antes de montar la app");
  }
  return instancia;
}

/**
 * Traduce un fallo de arranque del SSO a un mensaje accionable.
 *
 * `keycloak-js` rechaza con objetos vacíos o `undefined` en varios escenarios
 * (red caída, realm inexistente, contexto inseguro), así que un `String(error)`
 * pelado deja al usuario con "[object Object]" en pantalla.
 */
function describirError(error: unknown): ErrorSso {
  if (!window.isSecureContext) {
    return {
      titulo: "Contexto no seguro",
      detalle:
        "El inicio de sesión institucional requiere HTTPS. Abra la aplicación " +
        "en https:// o en http://localhost.",
    };
  }

  const mensaje = error instanceof Error ? error.message : "";

  return {
    titulo: "No fue posible contactar el servidor de autenticación",
    detalle:
      `No se pudo establecer comunicación con ${env().KEYCLOAK_URL}. ` +
      "Verifique su conexión a la red institucional e intente nuevamente." +
      (mensaje ? ` (${mensaje})` : ""),
  };
}

/**
 * Inicializa Keycloak y exige sesión antes de continuar.
 *
 * `onLoad: "login-required"` — y no `"check-sso"` — porque **toda** la
 * aplicación vive detrás del login: no quedó ninguna pantalla pública ni
 * formulario de respaldo desde que Keycloak es el único método de
 * autenticación (KEYCLOAK_LOGIN_STANDARD.md §3.4). Si ya existe sesión en el
 * realm (por haber entrado a otra app del workspace), el usuario vuelve
 * autenticado sin ver ninguna pantalla.
 *
 * Excepción con `REDIRECT_TO_APPS=true`: en vez de exigir sesión con el login
 * nativo, se hace un chequeo silencioso (`check-sso`) y, si no hay sesión en el
 * realm, se manda al usuario al portal de apps para que elija desde ahí por
 * dónde entrar — el mismo destino que el logout.
 *
 * Debe llamarse ANTES de montar React (KEYCLOAK_SSO_SETUP.md §2.5): si la
 * sesión se resolviera después del montaje, la app alcanzaría a renderizar una
 * vista sin autenticar.
 */
export async function initKeycloak(): Promise<void> {
  const faltantes = getConfigFaltante();
  if (faltantes.length > 0) {
    throw {
      titulo: "SSO no configurado",
      detalle:
        `Faltan variables de entorno: ${faltantes.join(", ")}. ` +
        "Copie .env.example a .env y complete los valores del realm.",
    } satisfies ErrorSso;
  }

  const { KEYCLOAK_URL, KEYCLOAK_REALM, KEYCLOAK_CLIENT_ID } = env();

  instancia = new Keycloak({
    url: KEYCLOAK_URL ?? "",
    realm: KEYCLOAK_REALM ?? "",
    clientId: KEYCLOAK_CLIENT_ID ?? "",
  });

  try {
    await instancia.init({
      onLoad: redirigirAlPortal() ? "check-sso" : "login-required",
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      pkceMethod: "S256",
      checkLoginIframe: true,
    });
  } catch (error) {
    throw describirError(error);
  }

  if (redirigirAlPortal() && !instancia.authenticated) {
    window.location.href = getSsoAppsUrl();
    // Deja al llamador esperando: no hay nada autenticado que renderizar
    // mientras el navegador completa la salida hacia el portal.
    return new Promise(() => {});
  }

  programarRenovacion();
}

const suscriptoresToken = new Set<() => void>();

/**
 * Cualquier pérdida de sesión detectada mientras la app ya está abierta —token
 * sin poder renovarse, o cierre de sesión propagado desde otra pestaña/app del
 * realm— manda al usuario al portal de apps, igual que el logout explícito y el
 * ítem "Todas las Apps" del menú de usuario, en vez de reintentar el login
 * nativo de Keycloak dentro de esta misma app.
 */
export function irAlPortalDeApps(): void {
  window.location.href = getSsoAppsUrl();
}

/**
 * Mantiene vivo el access token mientras la app esté abierta.
 *
 * Dos disparadores complementarios: el intervalo renueva antes de que venza
 * (leer una conversación larga o redactar una consulta no genera requests), y
 * `onTokenExpired` cubre el caso del equipo suspendido, donde los timers no
 * corren y el token ya está vencido al volver.
 *
 * Si el refresh token también expiró, no hay sesión que recuperar:
 * {@link irAlPortalDeApps} en lugar de dejar la app con un token muerto que el
 * backend va a rechazar con 401 en la siguiente consulta.
 */
function programarRenovacion(): void {
  const keycloak = getKeycloak();

  const renovar = () => {
    keycloak.updateToken(MIN_VALIDITY_SEGUNDOS).catch(irAlPortalDeApps);
  };

  window.setInterval(renovar, INTERVALO_RENOVACION_MS);
  keycloak.onTokenExpired = renovar;

  // Cada renovación trae claims nuevos: si en Keycloak le otorgaron o quitaron
  // un rol, el token ya lo refleja pero React no tiene forma de enterarse.
  keycloak.onAuthRefreshSuccess = () => {
    for (const escuchar of suscriptoresToken) escuchar();
  };

  // La sesión se cerró en otra pestaña o en otra app del realm: el iframe de
  // estado (`checkLoginIframe`) lo detecta y aquí se refleja de inmediato.
  keycloak.onAuthLogout = irAlPortalDeApps;

  vigilarRegresoAPantalla();
}

function vigilarRegresoAPantalla(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;

    getKeycloak().updateToken(-1).catch(irAlPortalDeApps);
  });
}

/**
 * Avisa cada vez que el token se renueva con claims nuevos.
 *
 * Devuelve la función para cancelar la suscripción, de modo que sirva
 * directamente como cleanup de un `useEffect`.
 */
export function alRenovarToken(escuchar: () => void): () => void {
  suscriptoresToken.add(escuchar);
  return () => {
    suscriptoresToken.delete(escuchar);
  };
}

/**
 * Access token vigente para mandar al backend.
 *
 * Renueva primero si le queda poco: el backend valida la expiración contra el
 * JWKS y rechaza con 401 un token vencido por segundos.
 */
export async function getTokenVigente(): Promise<string | undefined> {
  const keycloak = getKeycloak();

  try {
    await keycloak.updateToken(MIN_VALIDITY_SEGUNDOS);
  } catch {
    // El refresh token también venció: no hay nada que renovar. Se devuelve el
    // token actual igual, y el 401 del backend dispara la salida al portal.
  }

  return keycloak.token;
}

/**
 * Cierra la sesión institucional, no solo la local.
 *
 * Omitir el `end_session_endpoint` de Keycloak dejaría viva la sesión del realm
 * y el chequeo silencioso de esta u otra app del workspace volvería a
 * autenticar al usuario sin pedirle nada (KEYCLOAK_LOGIN_STANDARD.md §5).
 *
 * Tras cerrar sesión no tiene sentido devolver al usuario a esta misma app
 * —volvería a pedirle login de inmediato—, así que el destino es el portal de
 * apps, igual que el ítem "Todas las Apps" del menú de usuario.
 */
export function logout(): void {
  getKeycloak().logout({ redirectUri: getSsoAppsUrl() });
}
